import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { Add, Remove, KeyboardArrowLeft } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

const CATEGORY_TABS = {
  "Соларни панели": 1,
  "Фасади": 2,
  "Индустриални": 3,
};

// ChemiTek colors
const COLORS = {
  primary: "#0D3B66",
  secondary: "#0061a6ff",
  accent: "#F4D35E",
  lightGray: "#F1F1F1",
  textDark: "#273649",
};

export default function AgentInfo() {
  const { abb } = useParams();
  const navigate = useNavigate();
  const { dispatch } = useCart();
  const isRowBased = useMediaQuery("(min-width:900px)");

  const [agent, setAgent] = useState(null);
  const [selectedPack, setSelectedPack] = useState("");
  const [qty, setQty] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    fetch("/db/agents.json")
      .then((res) => res.json())
      .then((data) => {
        const found = data.find(
          (a) => a.abb.trim().toLowerCase() === abb.trim().toLowerCase()
        );
        setAgent(found || null);
        if (found?.pack?.length) setSelectedPack(found.pack[0]);
      })
      .catch(console.error);
  }, [abb]);

  if (!agent) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color={COLORS.textDark}>
          Не са намерени данни за продукта.
        </Typography>
        <Button
          variant="outlined"
          sx={{ mt: 2, color: COLORS.primary, borderColor: COLORS.primary }}
          onClick={() => navigate("/agents")}
        >
          Върни се към препаратите
        </Button>
      </Box>
    );
  }

  const backToAgents = () => {
    navigate("/agents", {
      state: { selectedTab: CATEGORY_TABS[agent.category] ?? 0 },
    });
  };

  const addToCart = () => {
    const idx = agent.pack.indexOf(selectedPack);
    if (idx === -1) return;

    dispatch({
      type: "ADD",
      item: {
        id: `${agent.abb}-${selectedPack}`,
        name: agent.nameBulgarian,
        pack: selectedPack,
        qty,
        priceBGN: agent.priceNumber[idx],
        priceEUR: agent.priceNumberEuro[idx],
        image: "/" + agent.image,
      },
    });
    setSnackbarOpen(true);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      {/* BACK BUTTON */}
      <Button
        startIcon={<KeyboardArrowLeft />}
        onClick={backToAgents}
        sx={{
          mb: 3,
          color: COLORS.primary,
          textTransform: "none",
          fontWeight: 500,
        }}
      >
        Назад към {agent.category}
      </Button>

      <Box
        display="flex"
        flexDirection={isRowBased ? "row" : "column"}
        gap={4}
        alignItems="flex-start"
      >
        {/* LEFT COLUMN - IMAGE */}
        <Card elevation={0} sx={{ flex: 1 }}>
          <CardMedia
            component="img"
            src={"/" + agent.image}
            alt={agent.nameBulgarian}
            sx={{
              maxWidth: 360,
              borderRadius: 2,
              objectFit: "contain",
            }}
          />
        </Card>

        {/* MIDDLE COLUMN - INFO & PACKS */}
        <Card sx={{ flex: 2 }} elevation={0}>
          <CardContent sx={{ padding: "16px" }}>
            <Typography
              variant="h5"
              color={COLORS.primary}
              fontWeight={600}
              textAlign="left"
              mb={0.5}
            >
              {agent.name}
            </Typography>
            <Typography
              variant="subtitle1"
              color={COLORS.textDark}
              textAlign="left"
              mb={1}
            >
              {agent.nameBulgarian}
            </Typography>

            <Typography
              variant="body2"
              color={COLORS.textDark}
              textAlign="left"
              mb={1}
            >
              Разреждане: {agent.dilution}
              <br />
              Опаковка: {agent.pack.join(", ")}
            </Typography>

            {agent.info && (
              <>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  mt={1}
                  textAlign="left"
                >
                  Как работи?
                </Typography>
                <Typography variant="body2" color={COLORS.textDark} textAlign="justify">
                  {agent.info.intro}
                </Typography>

                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  mt={1}
                  textAlign="left"
                >
                  Предимства
                </Typography>
                <Typography variant="body2" color={COLORS.textDark} textAlign="justify">
                  {agent.info.benefits}
                </Typography>

                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  mt={1}
                  textAlign="left"
                >
                  Приложимо за:
                </Typography>
                <Typography variant="body2" color={COLORS.textDark} textAlign="justify">
                  {agent.info.apply}
                </Typography>
              </>
            )}

            {/* PACK SELECTION */}
            <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
              {agent.pack.map((p, i) => (
                <Paper
                  key={p}
                  onClick={() => setSelectedPack(p)}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    borderRadius: 1,
                    bgcolor: selectedPack === p ? COLORS.secondary : COLORS.lightGray,
                    color: selectedPack === p ? "white" : COLORS.textDark,
                    fontSize: 13,
                    fontWeight: 500,
                    transition: "0.3s",
                    "&:hover": {
                      bgcolor: selectedPack === p ? COLORS.secondary : COLORS.accent,
                    },
                  }}
                >
                  {p} – {agent.priceNumber[i].toFixed(2)} лв /{" "}
                  {agent.priceNumberEuro[i].toFixed(2)} €
                </Paper>
              ))}
            </Box>

            {/* QUANTITY */}
            <Box display="flex" alignItems="center" mt={2} gap={1}>
              <IconButton
                size="small"
                sx={{ color: COLORS.primary }}
                onClick={() => setQty(Math.max(1, qty - 1))}
              >
                <Remove fontSize="small" />
              </IconButton>
              <Typography fontSize={14}>{qty}</Typography>
              <IconButton
                size="small"
                sx={{ color: COLORS.primary }}
                onClick={() => setQty(qty + 1)}
              >
                <Add fontSize="small" />
              </IconButton>
            </Box>

            <Button
              variant="contained"
              fullWidth
              sx={{
                mt: 2,
                py: 1.5,
                bgcolor: COLORS.secondary,
                "&:hover": { bgcolor: COLORS.primary },
              }}
              onClick={addToCart}
            >
              Добави в количката
            </Button>
          </CardContent>
        </Card>

        {/* RIGHT COLUMN - CERT ICONS */}
        <Card
          sx={{
            flex: 0.8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            p: 2,
            borderRadius: 2,
          }}
          elevation={0}
        >
          {["bio.png", "drops.jpg", "tuv-sud.png"].map((img, idx) => (
            <CardMedia
              key={idx}
              component="img"
              src={`/images/${img}`}
              alt={img}
              sx={{
                width: 60,
                height: 60,
                objectFit: "contain",
                borderRadius: 1,
                p: 0.5,
                bgcolor: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            />
          ))}
        </Card>
      </Box>

      {/* CONFIRMATION SNACKBAR */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity="success">Продуктът е добавен в количката</Alert>
      </Snackbar>
    </Box>
  );
}
